import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'dark' | 'light' | 'system';
export const PALETTES = ['orchid', 'ocean', 'emerald', 'violet', 'amber', 'teal'] as const;
export type Palette = (typeof PALETTES)[number];

export const PALETTE_LABELS: Record<Palette, { uz: string; swatch: string }> = {
  orchid: { uz: 'Orxideya', swatch: '#c56aa8' },
  ocean: { uz: 'Okean', swatch: '#3b82f6' },
  emerald: { uz: 'Zumrad', swatch: '#22b07d' },
  violet: { uz: 'Binafsha', swatch: '#8b6cf0' },
  amber: { uz: 'Qahrabo', swatch: '#e0902a' },
  teal: { uz: 'Firuza', swatch: '#14a3a3' },
};

const KEY = 'kts.theme';

function readStored(): { mode: ThemeMode; palette: Palette } {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as { mode?: ThemeMode; palette?: Palette };
      return {
        mode: v.mode === 'light' || v.mode === 'system' ? v.mode : 'dark',
        palette: v.palette && (PALETTES as readonly string[]).includes(v.palette) ? v.palette : 'orchid',
      };
    }
  } catch {
    /* localStorage yopiq bo'lishi mumkin (maxfiy oyna) */
  }
  return { mode: 'dark', palette: 'orchid' };
}

export function resolveMode(mode: ThemeMode): 'dark' | 'light' {
  if (mode !== 'system') return mode;
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyTheme(mode: ThemeMode, palette: Palette): void {
  const el = document.documentElement;
  el.dataset.theme = resolveMode(mode);
  el.dataset.palette = palette;
}

/** Ilova ishga tushishidan oldin chaqiriladi (miltillashsiz). */
export function bootTheme(): void {
  const { mode, palette } = readStored();
  applyTheme(mode, palette);
}

export function useTheme() {
  const [state, setState] = useState(readStored);

  useEffect(() => {
    applyTheme(state.mode, state.palette);
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* e'tiborsiz */
    }
    if (state.mode !== 'system') return;
    const mq = matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyTheme('system', state.palette);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [state]);

  const setMode = useCallback((mode: ThemeMode) => setState((s) => ({ ...s, mode })), []);
  const setPalette = useCallback((palette: Palette) => setState((s) => ({ ...s, palette })), []);
  const toggle = useCallback(
    () => setState((s) => ({ ...s, mode: resolveMode(s.mode) === 'dark' ? 'light' : 'dark' })),
    [],
  );
  return { mode: state.mode, resolved: resolveMode(state.mode), palette: state.palette, setMode, setPalette, toggle, setAll: setState };
}
